import React from "react";
import DlReportScreen from "./DlReportScreen";
import { formatAmount, formatToISTDate } from "../../../utils/utils";

/**
 * The Daily Loan Master report.
 *
 * Every figure comes from the server, which computes them all with one shared
 * builder - so this report and the DL Outstanding and Collection reports cannot
 * quote different numbers for the same loan. That is what report-audit.md means
 * by reconciling with Master.
 *
 * NOTHING HERE MAY SAY "MONTHS". DL stores its instalment count in a column
 * called tenureInMonths because the column is shared with the monthly products,
 * but a daily loan runs for 100 to 125 DAYS. The server sends tenureInDays for
 * exactly this reason.
 */
const columns = [
  { label: "Loan Acc No", render: (row) => row?.loanId || "-" },
  { label: "Customer Name", render: (row) => row?.memberName || "-" },
  { label: "Customer ID", render: (row) => row?.customerId || "-" },
  { label: "Phone", render: (row) => row?.phoneNumber || "-" },
  { label: "Branch", render: (row) => row?.branchName || "-" },
  { label: "Employee", render: (row) => row?.employeeName || row?.username || "-" },
  { label: "Funding Agency", render: (row) => row?.agencyName || "-" },
  { label: "Loan Cycle", render: (row) => row?.loanCycle ?? 1 },

  { label: "Loan Amount", render: (row) => formatAmount(row?.loanAmount || 0) },
  { label: "Rate %", render: (row) => row?.rateOfInterest ?? 0 },
  // Days, never months.
  { label: "Tenure (Days)", render: (row) => row?.tenureInDays ?? 0 },
  { label: "Daily Instalment", render: (row) => formatAmount(row?.dailyInstalment || 0) },
  { label: "Total Interest", render: (row) => formatAmount(row?.totalInterest || 0) },
  { label: "Total Repayable", render: (row) => formatAmount(row?.totalRepayable || 0) },

  { label: "Processing Fee", render: (row) => formatAmount(row?.processingCharge || 0) },
  { label: "GST", render: (row) => formatAmount(row?.gst || 0) },
  // DL calls this "Safety Fund" everywhere it's shown to a user - only the
  // column label changed, `row.securityDeposit` is still the same underlying
  // field computeDlCharges (dlCalculations.js) computes. BL/GPL/JLG keep
  // "Security Deposit" - they are separate label strings in separate files.
  { label: "Safety Fund", render: (row) => formatAmount(row?.securityDeposit || 0) },
  { label: "Insurance", render: (row) => formatAmount(row?.insuranceAmount || 0) },
  { label: "Amount In Hand", render: (row) => formatAmount(row?.amountToRelease || 0) },

  {
    label: "Disbursed Date",
    render: (row) => (row?.disbursedDate ? formatToISTDate(row.disbursedDate) : "-"),
  },
  {
    label: "First Collection",
    render: (row) => (row?.firstEmiDate ? formatToISTDate(row.firstEmiDate) : "-"),
  },
  {
    label: "Last Collection",
    render: (row) => (row?.lastEmiDate ? formatToISTDate(row.lastEmiDate) : "-"),
  },
  { label: "Collection Time", render: (row) => row?.collectionTime || "-" },

  // Days paid, not months.
  { label: "Days Collected", render: (row) => row?.emiDaysPaid ?? 0 },
  { label: "Day Of Term", render: (row) => (row?.dayNumber ?? "-") },
  { label: "Total Collected", render: (row) => formatAmount(row?.totalEmiPaid || 0) },
  { label: "Principal Paid", render: (row) => formatAmount(row?.principalPaid || 0) },
  { label: "Interest Paid", render: (row) => formatAmount(row?.interestPaid || 0) },
  {
    label: "Outstanding Principal",
    render: (row) => formatAmount(row?.totalOutstandingPrincipal || 0),
  },
  {
    label: "Outstanding Interest",
    render: (row) => formatAmount(row?.totalOutstandingInterest || 0),
  },

  { label: "Overdue Principal", render: (row) => formatAmount(row?.overduePrincipal || 0) },
  { label: "Overdue Interest", render: (row) => formatAmount(row?.overdueInterest || 0) },
  { label: "PAR", render: (row) => formatAmount(row?.totalPAR || 0) },
  { label: "DPD", render: (row) => row?.dpd ?? 0 },
  { label: "Bucket", render: (row) => row?.bucket || "-" },
  {
    label: "Last Paid Date",
    render: (row) => (row?.lastPaidDate ? formatToISTDate(row.lastPaidDate) : "-"),
  },
  { label: "Status", render: (row) => row?.loanStatus || "-" },
];

const DlMasterReport = () => (
  <DlReportScreen
    title="DL Master Report"
    endpoint="getDlMasterReport"
    reportName="DlMasterReport"
    columns={columns}
  />
);

export default DlMasterReport;
